import config from 'configs/app';

type RpcTx = {
  from?: string;
  to?: string;
  input?: string;
  value?: string;
  gas?: string;
  blockNumber?: string;
};

function decodeErrorString(data?: string): string | undefined {
  if (!data || !data.startsWith('0x')) {
    return undefined;
  }

  // Error(string) selector = 0x08c379a0
  if (data.slice(0, 10).toLowerCase() !== '0x08c379a0' || data.length < 138) {
    return undefined;
  }

  try {
    const length = parseInt(data.slice(74, 138), 16);
    if (!Number.isFinite(length) || length <= 0) {
      return undefined;
    }
    const hex = data.slice(138, 138 + length * 2);
    const bytes = hex.match(/.{1,2}/g) || [];
    return bytes.map((b) => String.fromCharCode(parseInt(b, 16))).join('').replace(/\0/g, '').trim() || undefined;
  } catch {
    return undefined;
  }
}

async function rpcCall(method: string, params: unknown[]): Promise<unknown> {
  const rpcUrl = config.chain.rpcUrls?.[0];
  if (!rpcUrl) {
    return null;
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
      signal: controller.signal,
    });
    const json = await res.json() as { result?: unknown; error?: { message?: string; data?: string } };
    if (json.error) {
      return json.error;
    }
    return json.result ?? null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * When Blockscout has not indexed revert_reason yet, recover it via eth_call
 * at the parent block (same calldata as the failed tx).
 */
export default async function fetchTxRevertMessage(hash: string): Promise<string | undefined> {
  const tx = await rpcCall('eth_getTransactionByHash', [ hash ]) as RpcTx | null;
  if (!tx?.to || !tx.input || !tx.blockNumber) {
    return undefined;
  }

  const parentBlock = `0x${ (parseInt(tx.blockNumber, 16) - 1).toString(16) }`;
  const callResult = await rpcCall('eth_call', [ {
    from: tx.from,
    to: tx.to,
    data: tx.input,
    value: tx.value || '0x0',
    gas: tx.gas,
  }, parentBlock ]);

  if (!callResult || typeof callResult !== 'object') {
    return undefined;
  }

  const error = callResult as { message?: string; data?: string };
  const fromData = decodeErrorString(error.data);
  if (fromData) {
    return fromData;
  }

  const message = error.message || '';
  const match = message.match(/execution reverted(?::\s*)?(.*)$/i);
  if (match?.[1]?.trim()) {
    return match[1].trim();
  }
  if (/execution reverted/i.test(message)) {
    return 'Execution reverted';
  }

  return undefined;
}
